@if (file()?.isGroupHeader) {
  <!-- Group Header -->
  <div
    class="bg-primary-container mt-2 flex items-center gap-2 rounded-md px-3 py-2"
  >
    <span class="text-lg font-semibold">{{ file()?.name }}</span>
  </div>
} @else {
  <!-- File Card Item -->
  <div
    class="surface-low sia-hover flex w-full flex-col rounded-lg border-solid p-2 transition-all duration-150 @container @sm:p-3"
    [class.border]="!isSelected()"
    [class.border-2]="isSelected()"
    [class.border-[var(--mat-sys-outline)]]="!isSelected() && !isRelevant()"
    [style.border-color]="
      isRelevant() && !isSelected() ? 'var(--ai-shade-2)' : ''
    "
    [class.border-primary]="isSelected()"
    [style.background]="
      isSelected()
        ? 'color-mix(in srgb, var(--mat-sys-primary) 12%, transparent) !important'
        : isRelevant()
          ? 'color-mix(in srgb, var(--ai-shade-2) 8%, transparent) !important'
          : ''
    "
    (click)="onRowClick()"
  >
    <!-- Main Row -->
    <div class="flex w-full flex-row items-center gap-2 @sm:gap-3">
      <!-- Checkbox -->
      @if (viewMode() == 'standard') {
        <div
          class="flex items-center p-1 @sm:p-4"
          [siaTooltip]="!file()?.hasChunks ? 'File non ancora elaborato, non disponibile per la chat' : ''"
          (click)="$event.stopPropagation(); file()?.hasChunks && toggleSelection()"
        >
          <input
            #checkbox
            type="checkbox"
            class="h-5 w-5 rounded border-2"
            [class.cursor-pointer]="file()?.hasChunks"
            [class.cursor-not-allowed]="!file()?.hasChunks"
            [class.opacity-40]="!file()?.hasChunks"
            [checked]="isChecked()"
            [disabled]="!file()?.hasChunks"
            (click)="$event.stopPropagation()"
            (change)="toggleSelection()"
          />
        </div>
      }

      <!-- Preview Area -->
      <div
        class="surface group relative flex h-[3.5rem] w-[3rem] flex-shrink-0 !cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-solid border-[var(--mat-sys-outline)] @sm:w-[4rem]"
        (click)="$event.stopPropagation(); onImgClick()"
      >
        @if (
          file()!.previewUrl | filePreview: file()!.name : file()!.type | async;
          as preview
        ) {
          @if (preview?.type === 'url') {
            <img
              [src]="preview!.url"
              [alt]="file()?.name || 'Preview'"
              class="pointer-events-none h-full w-full object-cover"
            />
            <!-- Hover overlay for previewable images -->
            <div
              class="pointer-events-none absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 opacity-0 transition-opacity duration-200 @sm:group-hover:opacity-100"
            >
              <mat-icon class="text-white">zoom_in</mat-icon>
            </div>
          } @else if (preview?.type === 'icon') {
            <mat-icon
              class="pointer-events-none"
              [style.color]="preview?.color"
            >
              {{ preview?.icon }}
            </mat-icon>
            <!-- Hover overlay for downloadable files -->
            <div
              class="absolute inset-0 flex cursor-pointer items-center justify-center bg-black bg-opacity-60 opacity-0 transition-opacity duration-200 @sm:group-hover:opacity-100"
            >
              <mat-icon class="pointer-events-none text-white"
                >zoom_in</mat-icon
              >
            </div>
          }
        } @else {
          <mat-spinner diameter="24"></mat-spinner>
        }
      </div>

      <!-- Column 1: Nome file -->
      <div class="flex flex-1 flex-col gap-1 overflow-hidden">
        <span class="truncate text-sm font-semibold" [title]="file()?.name">{{
          file()?.name
        }}</span>

        <span
          class="w-fit max-w-full truncate text-xs hover:underline"
          (click)="
            copyToClipboard(file()!.paths![0], 'Percorso copiato');
            $event.stopPropagation()
          "
          [style.direction]="'rtl'"
        >
          @if (file()!.paths!.length > 1) {
            <span class="text-primary" [siaTooltip]="file()!.paths!.join('\n')">
              {{ file()!.paths!.length - 1 }}+
            </span>
          }
          <bdi>
            {{ file()!.paths![0] || '—' }}
          </bdi>
        </span>
      </div>
      @for (
        entry of getExtractedPropertiesEntries(file()?.extractedProperties);
        track entry.key
      ) {
        <div
          class="hidden w-[200px] flex-shrink-0 flex-col gap-1 @[1500px]:flex"
        >
          @if (entry.value.value | hasExtractedValue) {
            <span class="text-outline text-xs font-semibold">{{
              entry.key
            }}</span>
            <span class="truncate text-sm">{{
              entry.value.value | formatExtractedValue
            }}</span>
          }
        </div>
      }
      <!-- Column 2: Categorie -->
      <div class="hidden w-[300px] flex-shrink-0 flex-col gap-1 @[1300px]:flex">
        <span class="text-outline text-xs font-semibold">Categorie</span>
        <div class="flex flex-wrap gap-1 overflow-x-auto">
          @if (file()?.categories && file()!.categories!.length > 0) {
            @for (category of file()?.categories; track $index) {
              <div
                [style.background]="category?.color || '#17749cff'"
                [colorContrastAuto]="true"
                contrastMode="adaptive"
                class="flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-bold"
              >
                <mat-icon class="!text-base">{{
                  category?.icon || 'folder'
                }}</mat-icon>
                <span>{{ category?.description || category?.id || '' }}</span>
              </div>
            }
          } @else {
            <span class="text-sm">—</span>
          }
        </div>
      </div>

      <!-- Column 3: Dimensione -->
      <div class="hidden w-[120px] flex-shrink-0 flex-col gap-1 @[1000px]:flex">
        <span class="text-outline text-xs font-semibold">Dimensione</span>
        <span class="text-sm">{{
          file()?.size ? (file()?.size | convertBytes) : '—'
        }}</span>
      </div>

      <!-- Column 4: Data ult. modifica -->
      <div class="hidden w-[150px] flex-shrink-0 flex-col gap-1 @[800px]:flex">
        <span class="text-outline text-xs font-semibold"
          >Data ult. modifica</span
        >
        <span class="text-sm">{{
          file()?.lastModifiedDate
            ? (file()?.lastModifiedDate | date: 'dd/MM/yy HH:mm')
            : '—'
        }}</span>
      </div>
      <!-- Expand/Collapse Button with Label -->
      <div
        class="flex flex-shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 hover:bg-[var(--mat-sys-surface-container-highest)] @sm:gap-2 @sm:py-1 @sm:pl-2"
        (click)="$event.stopPropagation(); toggleExpanded()"
      >
        <span
          class="hidden text-sm font-semibold @sm:block"
          [class.text-primary]="!isRelevant()"
          [style.color]="
            !isSelected() && isRelevant() ? 'var(--ai-shade-2)' : ''
          "
          >Dettagli</span
        >
        <button
          mat-icon-button
          class="pointer-events-none h-8 w-8 cursor-pointer"
          [class.!text-primary]="!isRelevant()"
          [style.color]="
            !isSelected() && isRelevant() ? 'var(--ai-shade-2)' : ''
          "
        >
          <mat-icon
            class="transition-transform duration-300"
            [class.rotate-180]="isExpanded()"
          >
            expand_more
          </mat-icon>
        </button>
      </div>

      <!-- Arrow Button (solo in modalità dossier) -->
      @if (viewMode() === 'dossier') {
        <button mat-icon-button class="flex-shrink-0" (click)="addFile()">
          <mat-icon>chevron_right</mat-icon>
        </button>
      }
    </div>

    <!-- Expandable Section -->
    <div
      class="overflow-hidden transition-all duration-300 ease-in-out"
      [style.height]="isExpanded() ? 'auto' : '0px'"
      [style.opacity]="isExpanded() ? '1' : '0'"
    >
      <!-- Divider -->
      <div class="my-2 h-[.5px] bg-[var(--mat-sys-outline)]"></div>

      <!-- Additional Data Section -->
      <div class="p-4">
        <div class="grid grid-cols-2 gap-3 md:grid-cols-2 lg:grid-cols-3">
          <div class="flex flex-col gap-1">
            <span class="text-outline text-xs font-semibold"
              >Data creazione</span
            >
            <span class="truncate text-sm">{{
              file()?.creationDate
                ? (file()?.creationDate | date: 'dd/MM/yy HH:mm')
                : '—'
            }}</span>
          </div>
          <!-- Categories - Only shown in details view when hidden in row -->
          <div class="flex flex-col gap-1 @[1300px]:hidden">
            <span class="text-outline text-xs font-semibold">Categorie</span>
            <div class="flex flex-wrap gap-1">
              @if (file()?.categories && file()!.categories!.length > 0) {
                @for (category of file()?.categories; track $index) {
                  <div
                    [style.background]="category?.color || '#17749cff'"
                    [colorContrastAuto]="true"
                    contrastMode="adaptive"
                    class="flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-bold"
                  >
                    <mat-icon class="!text-base">{{
                      category?.icon || 'folder'
                    }}</mat-icon>
                    <span>{{
                      category?.description || category?.id || ''
                    }}</span>
                  </div>
                }
              } @else {
                <span class="text-sm">—</span>
              }
            </div>
          </div>

          <!-- Size Information - Only shown in details view when hidden in row -->
          <div class="flex flex-col gap-1 @[1000px]:hidden">
            <span class="text-outline text-xs font-semibold">Dimensione</span>
            <span class="truncate text-sm">{{
              file()?.size ? (file()?.size | convertBytes) : '—'
            }}</span>
          </div>

          <!-- Date Information - Only shown in details view when hidden in row -->
          <div class="flex flex-col gap-1 @[800px]:hidden">
            <span class="text-outline text-xs font-semibold"
              >Data ultima modifica</span
            >
            <span class="truncate text-sm">{{
              file()?.lastModifiedDate
                ? (file()?.lastModifiedDate | date: 'dd/MM/yy HH:mm')
                : '—'
            }}</span>
          </div>
          <!-- Extracted Properties Loop -->
          @for (
            entry of getExtractedPropertiesEntries(
              file()?.completeExtractedProperties
            );
            track entry.key
          ) {
            @if (entry.value.value | hasExtractedValue) {
              <div class="flex flex-col gap-1">
                <span class="text-outline text-xs font-semibold">{{
                  entry.key
                }}</span>
                <span class="truncate text-sm">{{
                  entry.value.value | formatExtractedValue
                }}</span>
              </div>
            }
          }
        </div>
      </div>
    </div>
  </div>
}
